import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Try to get user from session (cookie) or token
      const savedToken = localStorage.getItem("token");
      const headers = savedToken ? { Authorization: `Bearer ${savedToken}` } : {};
      
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers,
        withCredentials: true  // Send cookies
      });
      setUser(response.data);
      if (savedToken) {
        setToken(savedToken);
      }
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser({ ...userData, auth_type: 'email' });
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser({ ...userData, auth_type: 'email' });
    return userData;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Master admin bypass — validates the secret server-side, returns a JWT token
  const loginAsAdmin = async (secret) => {
    const response = await axios.post(`${API_URL}/admin/bypass`, { secret });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    flushSync(() => {
      setToken(access_token);
      setUser({ ...userData, auth_type: 'email', is_admin: true });
    });
    return userData;
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/google/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      if (response.data.success) {
        // flushSync ensures state updates are committed BEFORE navigation happens,
        // preventing ProtectedRoute from seeing null user on first render
        flushSync(() => {
          if (response.data.access_token) {
            localStorage.setItem("token", response.data.access_token);
            setToken(response.data.access_token);
          }
          setUser({ ...response.data.user, auth_type: 'google' });
        });
        return response.data.user;
      }
      throw new Error("Failed to authenticate");
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    const authType = user?.auth_type;
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    return authType;
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const updateUserCredits = (newCredits) => {
    setUser(prev => prev ? { ...prev, credits: newCredits } : prev);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      loginWithGoogle,
      loginAsAdmin,
      handleGoogleCallback,
      getAuthHeader,
      checkAuth,
      updateUserCredits
    }}>
      {children}
    </AuthContext.Provider>
  );
};
